import {
  DayMap,
  MonthMap,
  ScheduleType,
  SubScheduleType,
} from "constants/constants";
import { ParsedCronSchedule } from "types";

// Helper function to get ordinal (1st, 2nd, 3rd, etc.)
export const getOrdinal = (num: number): string => {
  if (isNaN(num) || num < 1) return ""; // Prevent NaN issues
  const suffixes = ["th", "st", "nd", "rd"];
  const value = num % 100;
  return num + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
};

/**
 * Converts a parsed cron schedule into a human-readable string.
 *
 * @param parsedSchedule - The parsed cron schedule object.
 * @returns A human-readable string representation of the schedule.
 *
 * The function handles different types of schedules:
 * - Daily schedules with subtypes:
 *   - Every N day(s)
 *   - Every weekday (Mon-Fri)
 * - Weekly schedules
 * - Monthly schedules with subtypes:
 *   - Every N month(s) with Nth day
 *   - Every N month(s) with specific day
 * - Yearly schedules with subtypes:
 *   - Every N month(s) with Nth day yearly
 *   - Every N month(s) with specific day yearly
 *
 * If the schedule type or subtype is not recognized, an empty string is returned.
 *
 * @throws Will return an empty string if an error occurs during processing.
 */
export const parasedScheduleToReadable = (
  parsedSchedule: ParsedCronSchedule
): string => {
  try {
    const { type, subType, dayNumber, days, monthNumber, interval, nth } =
      parsedSchedule;
    const ordinal = getOrdinal(nth);
    const day = DayMap[dayNumber];
    const month = MonthMap[monthNumber];

    if (
      type === ScheduleType.DAILY &&
      subType === SubScheduleType.EVERY_N_DAY
    ) {
      return `At ${parsedSchedule.time}, every ${interval} day(s)`;
    }
    if (
      type === ScheduleType.DAILY &&
      subType === SubScheduleType.EVERY_WEEK_DAY
    ) {
      return `At ${parsedSchedule.time}, every weekday (Mon-Fri)`;
    }
    if (type === ScheduleType.WEEKLY) {
      return `At ${parsedSchedule.time}, every ${days
        .map((day) => DayMap[day])
        .join(", ")}`;
    }
    if (
      type === ScheduleType.MONTHLY &&
      subType === SubScheduleType.EVERY_N_MONTH_WITH_NTH_DAY
    ) {
      return `At ${parsedSchedule.time}, on the ${ordinal} day of every ${interval} month(s)`;
    }
    if (
      type === ScheduleType.MONTHLY &&
      subType === SubScheduleType.EVERY_N_MONTH_WITH_SPECIFIC_DAY
    ) {
      return `At ${parsedSchedule.time}, on the ${ordinal} ${day} of every ${interval} month(s)`;
    }
    if (
      type === ScheduleType.YEARLY &&
      subType === SubScheduleType.EVERY_N_MONTH_WITH_NTH_DAY_YEARLY
    ) {
      return `At ${parsedSchedule.time}, on the ${ordinal} day of ${month}, every year`;
    }
    if (
      type === ScheduleType.YEARLY &&
      subType === SubScheduleType.EVERY_N_MONTH_WITH_SPECIFIC_DAY_YEARLY
    ) {
      return `At ${parsedSchedule.time}, on the ${ordinal} ${day} of ${month}, every year`;
    }

    return "";
  } catch (error) {
    return "";
  }
};

export const parseCronExpression = (
  cronExpression: string
): ParsedCronSchedule => {
  try {
    if (cronExpression === "") {
      return {
        type: ScheduleType.DAILY,
        subType: SubScheduleType.EVERY_N_DAY,
        time: "00:00",
        days: [],
        months: [],
        interval: 1,
        dayNumber: 1,
        monthNumber: 1,
        nth: 1,
      };
    }
    const parts = cronExpression.split(" "); // [minute, hour, dayOfMonth, month, dayOfWeek]
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    const formattedTime = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;

    let type: ScheduleType = ScheduleType.DAILY;
    let subType: SubScheduleType = SubScheduleType.EVERY_N_DAY;
    let days: number[] = [];
    let months: number[] = [];
    let interval = 1;
    let dayNumber = 1;
    let monthNumber = 1;
    let nth = 1;

    if (month === "*") {
      // daily and weekly
      // **Every Days (e.g., "*")**
      if (dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
        type = ScheduleType.DAILY;
        subType = SubScheduleType.EVERY_N_DAY;
        interval = 1;
      } else if (
        dayOfMonth === "*" &&
        (dayOfWeek === "1-5" || dayOfWeek === "1,2,3,4,5")
      ) {
        // every week day
        type = ScheduleType.DAILY;
        subType = SubScheduleType.EVERY_WEEK_DAY;
        days = [1, 2, 3, 4, 5];
      } else if (
        dayOfMonth.includes("/") &&
        month === "*" &&
        dayOfWeek === "*"
      ) {
        // every n days
        type = ScheduleType.DAILY;
        subType = SubScheduleType.EVERY_N_DAY;
        interval = parseInt(dayOfMonth.split("/")[1], 10);
      } else if (dayOfMonth === "*" && dayOfWeek !== "*") {
        // specific days of the week
        type = ScheduleType.WEEKLY;
        subType = SubScheduleType.EVERY_N_DAY;
        days = dayOfWeek.split(",").map(Number);
      }
    } else {
      // monthly and yearly
      if (month.includes("*/")) {
        // monthly
        type = ScheduleType.MONTHLY;
        interval = parseInt(month.split("/")[1], 10);

        if (dayOfWeek.includes("#")) {
          subType = SubScheduleType.EVERY_N_MONTH_WITH_SPECIFIC_DAY;
          const [day, n] = dayOfWeek.split("#");
          dayNumber = parseInt(day, 10);
          nth = parseInt(n, 10);
        } else {
          subType = SubScheduleType.EVERY_N_MONTH_WITH_NTH_DAY;
          nth = parseInt(dayOfMonth, 10);
        }
      } else {
        // yearly
        type = ScheduleType.YEARLY;
        months = month.split(",").map(Number);
        monthNumber = months[0];

        if (dayOfWeek.includes("#")) {
          subType = SubScheduleType.EVERY_N_MONTH_WITH_SPECIFIC_DAY_YEARLY;
          const [day, n] = dayOfWeek.split("#");
          dayNumber = parseInt(day, 10);
          nth = parseInt(n, 10);
        } else {
          subType = SubScheduleType.EVERY_N_MONTH_WITH_NTH_DAY_YEARLY;
          nth = parseInt(dayOfMonth, 10);
        }
      }
    }

    return {
      type,
      subType,
      time: formattedTime,
      days,
      months,
      interval,
      dayNumber,
      monthNumber,
      nth,
    };
  } catch (error) {
    return {
      type: ScheduleType.DAILY,
      subType: SubScheduleType.EVERY_N_DAY,
      time: "00:00",
      days: [],
      months: [],
      interval: 1,
      dayNumber: 1,
      monthNumber: 1,
      nth: 1,
    };
  }
};

/**
 * Converts a parsed cron schedule object to a cron expression string.
 *
 * @param schedule - The parsed cron schedule object containing the schedule details.
 * @returns The cron expression string representing the schedule.
 *
 * The schedule object should have the following structure:
 * - `time`: A string representing the time in "HH:MM" format.
 * - `type`: The type of schedule (e.g., DAILY, WEEKLY, MONTHLY, YEARLY).
 * - `subType`: The subtype of the schedule, which varies based on the type.
 * - `interval`: The interval for the schedule (used for certain subtypes).
 * - `days`: An array of days (used for WEEKLY schedules).
 * - `dayNumber`: The day number (used for certain subtypes).
 * - `nth`: The nth occurrence (used for certain subtypes).
 * - `monthNumber`: The month number (used for certain subtypes).
 *
 * The function handles different schedule types and subtypes:
 * - DAILY:
 *   - EVERY_N_DAY: Runs every N days.
 *   - EVERY_WEEK_DAY: Runs every weekday (Mon-Fri).
 * - WEEKLY: Runs on specified days of the week.
 * - MONTHLY:
 *   - EVERY_N_MONTH_WITH_NTH_DAY: Runs on the Nth day of every N months.
 *   - EVERY_N_MONTH_WITH_SPECIFIC_DAY: Runs on a specific day of the week every N months.
 * - YEARLY:
 *   - EVERY_N_MONTH_WITH_NTH_DAY_YEARLY: Runs on the Nth day of a specific month every year.
 *   - EVERY_N_MONTH_WITH_SPECIFIC_DAY_YEARLY: Runs on a specific day of the week in a specific month every year.
 */
export const parsedCronScheduleToCron = (
  schedule: ParsedCronSchedule
): string => {
  const [hours, minutes] = schedule.time.split(":").map(Number);

  let cron = "";

  switch (schedule.type) {
    case ScheduleType.DAILY:
      if (schedule.subType === SubScheduleType.EVERY_N_DAY) {
        cron = `${minutes} ${hours} */${schedule.interval} * *`;
      } else if (schedule.subType === SubScheduleType.EVERY_WEEK_DAY) {
        cron = `${minutes} ${hours} * * 1-5`; // Weekdays (Mon-Fri)
      }
      break;

    case ScheduleType.WEEKLY:
      if (schedule.days.length > 0) {
        const days = schedule.days.join(",");
        cron = `${minutes} ${hours} * * ${days}`;
      }
      break;

    case ScheduleType.MONTHLY:
      if (schedule.subType === SubScheduleType.EVERY_N_MONTH_WITH_NTH_DAY) {
        cron = `${minutes} ${hours} ${schedule.nth} */${schedule.interval} *`;
      } else if (
        schedule.subType === SubScheduleType.EVERY_N_MONTH_WITH_SPECIFIC_DAY
      ) {
        cron = `${minutes} ${hours} * */${schedule.interval} ${schedule.dayNumber}#${schedule.nth}`;
      }
      break;

    case ScheduleType.YEARLY:
      if (
        schedule.subType === SubScheduleType.EVERY_N_MONTH_WITH_NTH_DAY_YEARLY
      ) {
        cron = `${minutes} ${hours} ${schedule.nth} ${schedule.monthNumber} *`;
      } else if (
        schedule.subType ===
        SubScheduleType.EVERY_N_MONTH_WITH_SPECIFIC_DAY_YEARLY
      ) {
        cron = `${minutes} ${hours} * ${schedule.monthNumber} ${schedule.dayNumber}#${schedule.nth}`;
      }
      break;
  }

  return cron;
};
